import React, { Component } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import getListingFormFields from 'common/listingFormFields';

import {
  getListingFullAddressString,
} from 'common/listingHelpers';

import {
  cleanMarketingPartnerListingFormValues,
} from 'common/marketingPartnerListingHelpers';

import {
  LISTING_OCCUPANT_TYPE_VACANT,
} from 'common/listingConstants';

import {
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE,
  PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
  PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
} from 'common/marketingPartnerListingConstants';

import {
  SR_API_ACTION_PARTNER_EDIT_LISTING,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_PARTNER_LISTINGS,
} from 'common/dataTableConstants';

import {
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';
import {
  dataTable,
  actionDataTableLoadItems,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';
import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import branch from 'client/core/branch';

import PartnerActions from 'client/actions/Partner';
import postWithState from 'client/actions/postWithState';

import {
  SCREEN_ID_PARTNER_LISTING_EDIT_SHOWING_ACCESS,
} from 'client/constants/screenIds';

import SrText from 'client/components/SrText';
import SrCard, { SR_CARD_BULLET_COLOR_YELLOW, SR_CARD_CONTENT_TYPE_FORM_REGULAR } from 'client/components/SrCard/SrCard';
import SrFooterButton from 'client/components/SrFooterButton/SrFooterButton';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrRouteLeavingGuard from 'client/components/SrRouteLeavingGuard';

const FormikContext = React.createContext();

const occupancyFields = [
  'occupantType',
  'vacantDate',
  'occupantName',
  'occupantPhone',
  'occupantEmail',
];

const propertyAccessFields = [
  'accessMethod',
  'propertySetupAccessType',
  'keyPickupDescription',
  'lockboxCode',
  'propertyAccessCode',
  'supraCbsCode',
  'dateKeyExpectedAtListing',
  'propertyAccessContactName',
  'propertyAccessContactPhone',
  'propertyAccessContactEmail',
  'gateCode',
  'alarmCode',
  'showingInstructions',
];

const photographyFields = [
  'requirePhotos',
  'linkToExistingPhotos',
  'availableForPhotoDate',
  'availableForPhotoDateSchedule',
  'availableForPhotoContactName',
  'availableForPhotoContactPhone',
];

const getFormFields = ({
  values = {},
} = {}) => {
  const shouldShowOccupantFields = (
    values.occupantType
    && values.occupantType !== LISTING_OCCUPANT_TYPE_VACANT
  );
  // eslint-disable-next-line max-len
  const shouldShowKeyContact = values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY
    && values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY;
  const shouldShowScheduleContact = (
    (values.requirePhotos || values.requireVirtual3dTour)
    && values.availableForPhotoDateSchedule === AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE
  );

  return [
    ...getListingFormFields({
      fieldgroup: occupancyFields,
      updates: {
        occupantType: {
          validations: ['required'],
        },
        vacantDate: {
          hideFieldIf: !shouldShowOccupantFields,
          validations: ['required'],
        },
        occupantName: {
          hideFieldIf: !shouldShowOccupantFields,
          title: `${values.occupantType} Name`,
          validations: ['required'],
        },
        occupantPhone: {
          hideFieldIf: !shouldShowOccupantFields,
          title: `${values.occupantType} Phone`,
          validations: ['required', 'phone'],
        },
        occupantEmail: {
          hideFieldIf: !shouldShowOccupantFields,
          title: `${values.occupantType} Email`,
          label: '(Optional)',
          validations: ['email'],
        },
      },
    }),
    ...getListingFormFields({
      fieldgroup: propertyAccessFields,
      updates: {
        accessMethod: {
          validations: ['required'],
        },
        propertySetupAccessType: {
          hideFieldIf: !values.accessMethod
            || values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
          validations: ['required'],
        },
        propertyAccessCode: {
          properties: {
            placeholder: '1234#',
          },
          hideFieldIf: !values.accessMethod
            || values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
          validations: ['required'],
        },
        supraCbsCode: {
          properties: {
            placeholder: '1234567',
          },
          hideFieldIf: !values.accessMethod
            || values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE
            || values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
          validations: ['required'],
        },
        dateKeyExpectedAtListing: {
          properties: {
            placeholder: 'MM/DD/YYYY',
          },
          hideFieldIf: !values.accessMethod
            || values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE
            || values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
          validations: ['required'],
        },
        lockboxCode: {
          label: null,
          properties: {
            placeholder: '2233',
          },
          hideFieldIf: !values.accessMethod
            || values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE
            || values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
          validations: ['required'],
        },
        keyPickupDescription: {
          title: 'Where is the key located?',
          type: 'long_text',
          properties: {
            placeholder: 'The key is located...',
          },
          hideFieldIf: !values.accessMethod
            || values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE
            || values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
          validations: ['required'],
        },
        propertyAccessContactName: {
          hideFieldIf: !shouldShowKeyContact,
          validations: ['required'],
        },
        propertyAccessContactPhone: {
          hideFieldIf: !shouldShowKeyContact,
          validations: ['required', 'phone'],
        },
        propertyAccessContactEmail: {
          hideFieldIf: !shouldShowKeyContact,
          validations: ['required', 'email'],
        },
        gateCode: {
          title: 'Gate Code',
          label: '(optional)',
          properties: {
            placeholder: '1234#',
          },
        },
      },
    }),
    ...getListingFormFields({
      fieldgroup: photographyFields,
      required: true,
      updates: {
        linkToExistingPhotos: {
          hideFieldIf: values.requirePhotos !== false,
        },
        availableForPhotoDate: {
          hideFieldIf: (
            values.requirePhotos !== true
            && values.requireVirtual3dTour !== true
          ),
        },
        availableForPhotoDateSchedule: {
          hideFieldIf: (
            values.requirePhotos !== true
            && values.requireVirtual3dTour !== true
          ),
        },
        availableForPhotoContactName: {
          hideFieldIf: !shouldShowScheduleContact,
        },
        availableForPhotoContactPhone: {
          hideFieldIf: !shouldShowScheduleContact,
          validations: ['phone'],
        },
      },
    }),
  ];
};

function getListingDataTableSettings({ partner, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'listing',
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId: `by-listingId-${listingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'listings.id': parseInt(listingId, 10),
      'listings.partnerId': partner.id,
    },
    customParams: {
      partnerListings: {},
    },
  };

  return dataTableSettings;
}

@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_LISTING_EDIT_SHOWING_ACCESS)
@withScrollToTop
@withApiState({
  partnerEditListingApiState: {
    action: SR_API_ACTION_PARTNER_EDIT_LISTING,
  },
})
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
}))
@dataTable(getListingDataTableSettings)
class PartnerEditListingShowingAccessPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Edit Listing | Showing & Access',
  })

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);

    const promises = lodashCompact([
      !partner && PartnerActions.loadPartnerBySlug(inject, {
        slug: match.params.partnerSlug,
      }),
    ]);

    lodashCompact([
      getListingDataTableSettings({ partner, match }),
    ]).forEach((dataTableSettings) => {
      actionInitializeDataTableState(inject, dataTableSettings);
      promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, dataTableSettings));
    });

    return Promise.all(promises);
  }

  static propTypes = {
    listing: PropTypes.object,
    partner: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    partnerEditListingApiState: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      editSuccessModalIsOpen: false,
    };
  }

  componentDidMount() {
    if (process.browser) {
      global.addEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  componentWillUnmount() {
    if (process.browser) {
      global.removeEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  _onBeforeUnload = (event) => {
    if (this.dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  _onSubmit = (formValues) => {
    const {
      partner,
      listing,
      dispatch,
    } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_EDIT_LISTING,
      payload: {
        partnerId: partner.id,
        listingId: listing.id,
        updates: cleanMarketingPartnerListingFormValues(formValues),
      },
      onSuccess: () => {
        this.setState({
          editSuccessModalIsOpen: true,
        });
        this._reloadPartnerListing();
      },
    });
  }

  _onCloseEditSuccessModal = () => {
    this.setState({
      editSuccessModalIsOpen: false,
    });
  }

  _reloadPartnerListing = () => {
    const { partner, match, dispatch } = this.props;

    return Promise.all([
      dispatch(actionDataTableLoadItems, getListingDataTableSettings({
        partner,
        match,
      })),
    ]);
  }

  render() {
    const {
      listing,
      partner,
      partnerEditListingApiState,
      dispatch,
    } = this.props;

    const {
      editSuccessModalIsOpen,
    } = this.state;

    if (!listing) {
      return null;
    }

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/listing/${listing.id}/edit`}
        navbarTitle={listing ? getListingFullAddressString(listing) : null}
        pageHeader='Showing & Access'
      >
        <Formik
          ref={(formik) => {
            this.formik = formik;
          }}
          initialValues={createInitialValues({
            formFields: getFormFields(),
            defaultValues: listing,
          })}
          enableReinitialize
          validate={(values) => {
            const formFields = getFormFields({ values });
            return createFormValidationFn(formFields)(values);
          }}
          onSubmit={this._onSubmit}
          render={(formikBag) => {
            const formFields = getFormFields({
              values: formikBag.values,
            });
            const getFormFieldsById = createFormFieldsByIdGetter(formFields);

            this.dirty = formikBag.dirty;

            return (
              <>
                <FormikContext.Provider value={formikBag}>
                  <Form>
                    <SrCard
                      color={SR_CARD_BULLET_COLOR_YELLOW}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                      header='Occupancy'
                    >
                      <SrBox>
                        {occupancyFields.map((fieldId) => (
                          <SrFormikField
                            key={fieldId}
                            formField={getFormFieldsById(fieldId)}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        ))}
                      </SrBox>
                    </SrCard>

                    <SrCard
                      color={SR_CARD_BULLET_COLOR_YELLOW}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                      header='Property Access'
                    >
                      <SrBox>
                        {propertyAccessFields.map((fieldId) => (
                          <SrFormikField
                            key={fieldId}
                            formField={getFormFieldsById(fieldId)}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        ))}
                      </SrBox>
                    </SrCard>

                    <SrCard
                      color={SR_CARD_BULLET_COLOR_YELLOW}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                      header='Photography'
                    >
                      <SrBox>
                        {photographyFields.map((fieldId) => (
                          <SrFormikField
                            key={fieldId}
                            formField={getFormFieldsById(fieldId)}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        ))}
                      </SrBox>
                    </SrCard>

                    <SrStaticFooter>
                      <SrText
                        display='block'
                        lineHeight='25px'
                        font='greycliff'
                        color='grey600'
                        fontSize='13px'
                        weight='500'
                      >
                        Updates can take 24 hours to syndicate
                      </SrText>
                      <SrFooterButton
                        hasBoxShadow={!partnerEditListingApiState.inProgress}
                        disabled={partnerEditListingApiState.inProgress}
                      >
                        {partnerEditListingApiState.inProgress ? 'Saving...' : 'Save'}
                      </SrFooterButton>
                    </SrStaticFooter>
                  </Form>
                </FormikContext.Provider>
                <SrRouteLeavingGuard
                  when={formikBag.dirty}
                  dispatch={dispatch}
                />
              </>
            );
          }}
        />
        {editSuccessModalIsOpen && (
          <SrSimpleMessageModal
            iconUrl='/images/checkmarkSuccessGreen.svg'
            headline={(
              <span>Successfully <br /> edited property!</span>
            )}
            message='Changes are live instantly on Sunroom.'
            buttonText='ok, thanks'
            maxWidth={375}
            onClose={this._onCloseEditSuccessModal}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerEditListingShowingAccessPage.displayName = 'PartnerEditListingShowingAccessPage';

export default PartnerEditListingShowingAccessPage;
